import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, FileText, Calendar, DollarSign, Building, Tag, Send, Clock, User, MapPin, Receipt, CheckCircle } from 'lucide-react';
import { ExpenseService, Expense } from '../services/ExpenseService';
import { useAuth } from '../contexts/AuthContext';
import WorkflowApprovalFlow from '../components/WorkflowApprovalFlow';
import ModernLayout from '../components/ModernLayout';
import { ModernCard, StatCard } from '../components/ModernCards';

const ExpenseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expense, setExpense] = useState<Expense | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchExpense = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const expenseData = await ExpenseService.getExpense(parseInt(id));
      setExpense(expenseData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch expense');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const handleDelete = async () => {
    if (!expense || !window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await ExpenseService.deleteExpense(expense.id);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to delete expense');
    }
  };

  const handleSubmit = async () => {
    if (!expense) return;
    
    // Validate that expense has required fields for submission
    if (!expense.category_id) {
      setError('Expense must have a category assigned before submission');
      return;
    }
    
    if (!expense.policy_id) {
      setError('Expense must have a policy assigned before submission');
      return;
    }
    
    if (!window.confirm('Are you sure you want to submit this expense for approval?')) return;
    
    try {
      await ExpenseService.submitExpense(expense.id);
      // Reload the expense to get updated status
      fetchExpense();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit expense');
    }
  };

  const formatCurrency = (amount: number | string, currency: string) => {
    // Convert string to number if needed
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Handle invalid amounts
    if (numericAmount === null || numericAmount === undefined || isNaN(numericAmount)) {
      return '$0.00';
    }
    
    // Map PYUSD to USD for display purposes since Intl.NumberFormat doesn't recognize PYUSD
    const displayCurrency = currency === 'PYUSD' ? 'USD' : currency;
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: displayCurrency,
      }).format(numericAmount);
    } catch (error) {
      // Fallback for any other invalid currency codes
      return `${currency} ${numericAmount.toFixed(2)}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'badge-info';
      case 'submitted':
        return 'badge-warning';
      case 'approved':
        return 'badge-success';
      case 'rejected':
        return 'badge-error';
      case 'sent_back':
        return 'badge-warning';
      case 'paid':
        return 'badge-paid';
      default:
        return 'badge-info';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <FileText className="h-4 w-4" />;
      case 'submitted':
        return <Clock className="h-4 w-4" />;
      case 'approved':
        return <Tag className="h-4 w-4" />;
      case 'rejected':
        return <Trash2 className="h-4 w-4" />;
      case 'sent_back':
        return <ArrowLeft className="h-4 w-4" />;
      case 'paid':
        return <CheckCircle className="h-4 w-4" />; // Check circle for completed payment
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <ModernLayout title="Loading..." subtitle="Fetching expense details...">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-green-600"></div>
        </div>
      </ModernLayout>
    );
  }

  if (error || !expense) {
    return (
      <ModernLayout title="Expense Not Found" subtitle="The requested expense could not be found.">
        <ModernCard className="p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Expense Not Found</h3>
          <p className="text-gray-600 mb-6">{error || 'The requested expense could not be found.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary"
          >
            Back to Dashboard
          </button>
        </ModernCard>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout
      title={expense.title}
      subtitle={`Expense Details • ${formatDate(expense.time)}`}
      actions={
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-ghost flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          {expense.status === 'draft' && expense.category_id && expense.policy_id && (
            <button
              onClick={handleSubmit}
              className="btn-primary flex items-center space-x-2"
            >
              <Send className="h-4 w-4" />
              <span>Submit for Approval</span>
            </button>
          )}
          {expense.status === 'draft' && (!expense.category_id || !expense.policy_id) && (
            <div className="flex items-center px-4 py-2 bg-gray-100 text-gray-500 rounded-lg">
              <Send className="h-4 w-4 mr-2" />
              <span>Assign Category & Policy to Submit</span>
            </div>
          )}
          <button
            onClick={() => navigate(`/expenses/${expense.id}/edit`)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Edit className="h-4 w-4" />
            <span>Edit</span>
          </button>
          <button
            onClick={handleDelete}
            className="btn-ghost text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center space-x-2"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Status and Amount Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Amount"
          value={formatCurrency((expense.usd_amount && expense.usd_amount > 0) ? expense.usd_amount : expense.amount, expense.currency)}
          subtitle={`Original: ${formatCurrency(expense.amount, expense.currency)}`}
          icon={<DollarSign className="h-6 w-6 text-green-600" />}
        />
        <StatCard
          title="Status"
          value={
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(expense.status)}`}>
              {getStatusIcon(expense.status)}
              <span className="ml-2">{expense.status.charAt(0).toUpperCase() + expense.status.slice(1)}</span>
            </span>
          }
          subtitle="Current status"
          icon={<Clock className="h-6 w-6 text-green-600" />}
        />
        <StatCard
          title="Source"
          value={expense.source.charAt(0).toUpperCase() + expense.source.slice(1)}
          subtitle="Transaction source"
          icon={<Building className="h-6 w-6 text-purple-600" />}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <ModernCard className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <p className="text-gray-900 font-medium">{expense.title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <p className="text-gray-900">{expense.expense_type?.charAt(0).toUpperCase() + expense.expense_type?.slice(1) || 'Regular'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <p className="text-gray-900">{formatDate(expense.time)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <p className="text-gray-900">{formatDate(expense.created_at)}</p>
              </div>
              {expense.description && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-gray-900">{expense.description}</p>
                </div>
              )}
            </div>
          </ModernCard>

          {/* Vendor Information */}
          {(expense.vendor || expense.location || expense.invoice_number) && (
            <ModernCard className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building className="h-5 w-5 mr-2 text-green-600" />
                Vendor Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {expense.vendor && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                    <p className="text-gray-900">{expense.vendor}</p>
                  </div>
                )}
                {expense.location && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <p className="text-gray-900 flex items-center">
                      <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                      {expense.location}
                    </p>
                  </div>
                )}
                {expense.invoice_number && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                    <p className="text-gray-900 font-mono">{expense.invoice_number}</p>
                  </div>
                )}
              </div>
            </ModernCard>
          )}

          {/* Receipt Information */}
          {expense.receipt_url && (
            <ModernCard className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Receipt className="h-5 w-5 mr-2 text-purple-600" />
                Receipt
              </h3>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Receipt className="h-8 w-8 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Receipt attached</p>
                  <a
                    href={expense.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 text-sm"
                  >
                    View receipt →
                  </a>
                </div>
              </div>
            </ModernCard>
          )}

          {/* Reimbursement Information */}
          {expense.reimbursement_status && (
            <ModernCard className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                Reimbursement Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    expense.reimbursement_status === 'completed' ? 'badge-paid' : 'badge-warning'
                  }`}>
                    {expense.reimbursement_status.charAt(0).toUpperCase() + expense.reimbursement_status.slice(1)}
                  </span>
                </div>
                {expense.reimbursement_amount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(expense.reimbursement_amount, 'USD')}
                    </p>
                  </div>
                )}
                {expense.reimbursement_tx_hash && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Hash</label>
                    <div className="flex items-center space-x-2">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono text-gray-800">
                        {expense.reimbursement_tx_hash}
                      </code>
                      <a
                        href={`https://solscan.io/tx/${expense.reimbursement_tx_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 text-sm"
                      >
                        View on Solscan →
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </ModernCard>
          )}

          {/* Workflow Approval Flow */}
          {expense.status !== 'draft' && (
            <ModernCard className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-orange-600" />
                Approval Workflow
              </h3>
              <WorkflowApprovalFlow expenseId={expense.id} />
            </ModernCard>
          )}
        </div>

        {/* Right Column - Additional Info */}
        <div className="space-y-6">
          {/* Category & Policy */}
          <ModernCard className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Tag className="h-5 w-5 mr-2 text-indigo-600" />
              Classification
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <p className="text-gray-900">
                  {expense.category_name || 'No category assigned'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Policy</label>
                <p className="text-gray-900">
                  {expense.policy_name || 'No policy assigned'}
                </p>
              </div>
            </div>
          </ModernCard>

          {/* Transaction Details */}
          <ModernCard className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-green-600" />
              Transaction Details
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Original Amount</label>
                <p className="text-gray-900 font-mono">{formatCurrency(expense.amount, expense.currency)}</p>
              </div>
              {expense.usd_amount && expense.usd_amount !== expense.amount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">USD Equivalent</label>
                  <p className="text-gray-900 font-mono">{formatCurrency(expense.usd_amount, 'USD')}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <p className="text-gray-900">{expense.currency}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <p className="text-gray-900">{expense.source.charAt(0).toUpperCase() + expense.source.slice(1)}</p>
              </div>
            </div>
          </ModernCard>

          {/* Timeline */}
          <ModernCard className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-green-600" />
              Timeline
            </h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Created</p>
                  <p className="text-xs text-gray-500">{formatDate(expense.created_at)}</p>
                </div>
              </div>
              {expense.submitted_at && (
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Submitted</p>
                    <p className="text-xs text-gray-500">{formatDate(expense.submitted_at)}</p>
                  </div>
                </div>
              )}
              {expense.approved_at && (
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Approved</p>
                    <p className="text-xs text-gray-500">{formatDate(expense.approved_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </ModernCard>
        </div>
      </div>
    </ModernLayout>
  );
};

export default ExpenseDetailPage;